import { Helmet } from "react-helmet-async";
import { getSiteUrl } from "@/config/site";
import {
  SITE,
  buildFullTitle,
  buildDescription,
  toAbsoluteUrl,
  robotsContent,
  jsonLdKey,
  normalizeJsonLd,
} from "./seo/seoUtils";
import useStripStaticSeoTags from "./seo/useStripStaticSeoTags";

/**
 * Central SEO/Head component. Injects title, description, canonical,
 * robots, Open Graph, Twitter Card, and JSON-LD schema tags.
 *
 * Absolute URLs are derived from getSiteUrl() so canonical and og:image
 * work correctly on any domain (preview or production).
 *
 * Dedupe strategy: static fallback tags in public/index.html carry the
 * `data-static-seo` marker. useStripStaticSeoTags removes them before paint
 * so react-helmet-async is the sole source of live SEO tags. Never remove
 * Helmet-managed nodes — that corrupts React's reconciler.
 *
 * All meta tags below are direct children of <Helmet> on purpose:
 * react-helmet-async needs literal children to index tag types correctly,
 * so we do NOT wrap groups (OG / Twitter / JSON-LD) in sub-components.
 */
export default function Seo({
  title,
  description,
  canonical,
  jsonLd,
  ogImage = "/images/job-1.webp",
  ogType = "website",
  noindex = false,
  keywords,
}) {
  const siteUrl = getSiteUrl();
  const fullTitle = buildFullTitle(title);
  const desc = buildDescription(description);
  const absoluteCanonical = toAbsoluteUrl(canonical, siteUrl);
  const absoluteOgImage = toAbsoluteUrl(ogImage, siteUrl);
  const ogImageAlt = `${SITE} — 24/7 tow truck near Kirkland & Bellevue, WA`;
  const twitterImageAlt = `${SITE} — 24/7 tow truck`;
  const ldItems = normalizeJsonLd(jsonLd);

  useStripStaticSeoTags();

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent(noindex)} />
      <link rel="canonical" href={absoluteCanonical} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={absoluteCanonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={absoluteOgImage} />
      <meta name="twitter:image:alt" content={twitterImageAlt} />

      {ldItems.map((obj) => (
        <script key={jsonLdKey(obj)} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
